import { useToast } from "@src/lib/hooks/useToast";
import { isHls } from "@src/lib/utils/files";
import { datadogRum } from "@datadog/browser-rum";
import Hls, { Events, ManifestLoadedData } from "hls.js";
import {
  FC,
  RefObject,
  VideoHTMLAttributes,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { cn } from "@src/lib/utils/cn";

type Props = {
  hideControls?: boolean;
  playOnHover?: boolean;
  _ref?: RefObject<HTMLVideoElement | null>;
  language?: Language;
  disableFastForward?: boolean;
  onVideoComplete?: () => void;
  src: VideoHTMLAttributes<HTMLVideoElement>["src"];
  videoProps?: VideoHTMLAttributes<HTMLVideoElement>;
};

// hls.js may advance the playhead slightly while recovering from a buffer gap.
// Avoid treating that internal recovery as user-initiated fast-forwarding.
const SEEK_AHEAD_TOLERANCE_SECONDS = 1;
const HLS_RECOVERY_DELAY_MS = 1000;
const MAX_HLS_RECOVERY_ATTEMPTS = 5;

const playedRanges = (video: HTMLVideoElement) =>
  Array.from({ length: video.played.length }, (_, index) => ({
    start: video.played.start(index),
    end: video.played.end(index),
  }));

const maxPlayedEnd = (video: HTMLVideoElement): number | null => {
  const ranges = playedRanges(video);
  if (ranges.length === 0) {
    return null;
  }
  return Math.max(...ranges.map(({ end }) => end));
};

const VideoPlayer: FC<Props> = (props) => {
  const _ref = useRef<HTMLVideoElement>(null);
  const ref = props._ref || _ref;
  const recoverySeekPositionRef = useRef<number | null>(null);
  const { addErrorToast } = useToast();
  const loadHls = useCallback(
    (startPosition?: number): Hls | null => {
      const video = ref.current;
      if (!Hls.isSupported() || !video) {
        return null;
      }
      const hls = new Hls(
        startPosition !== undefined && startPosition > 0
          ? { startPosition }
          : undefined,
      );
      hls.loadSource(String(props.src || ""));
      hls.attachMedia(video);
      const setSubtitleTrack = (
        _: Events.MANIFEST_LOADED,
        data: ManifestLoadedData,
      ) => {
        const subtitleTracks = data?.subtitles || [];
        const subtitleIndex = subtitleTracks.findIndex(
          (s) => s.lang === (props.language || "en") && s.type === "SUBTITLES",
        );
        hls.subtitleTrack = subtitleIndex;
      };
      hls.on(Hls.Events.MANIFEST_LOADED, setSubtitleTrack);
      return hls;
    },
    [props.src, props.language, ref],
  );

  useEffect(() => {
    if (props.disableFastForward) {
      const video = ref.current;
      if (!video) return;
      const enforcePlaybackRate = () => {
        if (video.playbackRate > 1) {
          addErrorToast({
            message: "Video must be played at 1x speed",
          });
          video.playbackRate = 1;
        }
      };
      video.playbackRate = 1;
      video.addEventListener("ratechange", enforcePlaybackRate);
      return () => {
        video.removeEventListener("ratechange", enforcePlaybackRate);
      };
    }
  }, [props.disableFastForward, ref, addErrorToast]);

  useEffect(() => {
    if (props.disableFastForward) {
      const video = ref.current;
      if (!video) return;
      const handleSeeking = () => {
        const recoverySeekPosition = recoverySeekPositionRef.current;
        if (
          recoverySeekPosition !== null &&
          Math.abs(video.currentTime - recoverySeekPosition) <=
            SEEK_AHEAD_TOLERANCE_SECONDS
        ) {
          recoverySeekPositionRef.current = null;
          return;
        }
        const playedEnd = maxPlayedEnd(video);
        if (
          playedEnd !== null &&
          video.currentTime > playedEnd + SEEK_AHEAD_TOLERANCE_SECONDS
        ) {
          addErrorToast({
            message: "Fast forwarding not allowed",
          });
          video.currentTime = playedEnd;
        }
      };
      video.addEventListener("seeking", handleSeeking);
      return () => {
        video.removeEventListener("seeking", handleSeeking);
      };
    }
  }, [props.disableFastForward, ref, addErrorToast]);

  useEffect(() => {
    let hls: Hls | null;
    let recoveryAttempts = 0;
    let recoveryTimeout: ReturnType<typeof setTimeout> | null = null;
    let isDisposed = false;
    if (!props.src) {
      console.warn("src not supplied to VideoPlayer");
      return;
    }
    if (ref.current) {
      const video = ref.current;
      // Prefer hls.js for HLS streams to handle subtitle tracks correctly,
      // since Chrome's native HLS player seems to occasionally not work.
      if (isHls(String(props.src)) && Hls.isSupported()) {
        const regenHls = (startPosition?: number) => {
          if (
            startPosition !== undefined &&
            recoveryAttempts >= MAX_HLS_RECOVERY_ATTEMPTS
          ) {
            return;
          }
          if (startPosition !== undefined) {
            recoveryAttempts++;
            recoverySeekPositionRef.current = startPosition;
          }
          if (hls) {
            hls.destroy();
          }
          hls = loadHls(startPosition);
          hls?.on(Hls.Events.ERROR, (_, data) => {
            const canScheduleRecovery =
              data.fatal &&
              recoveryTimeout === null &&
              recoveryAttempts < MAX_HLS_RECOVERY_ATTEMPTS;
            datadogRum.addAction("video_hls_error", {
              currentTime: video.currentTime,
              playedRanges: playedRanges(video),
              errorType: data.type,
              errorDetails: data.details,
              fatal: data.fatal,
              recoveryAttempt: canScheduleRecovery
                ? recoveryAttempts + 1
                : recoveryAttempts,
              recoveryScheduled: canScheduleRecovery,
            });
            // Non-fatal errors (e.g. bufferStalledError at a segment
            // boundary) are recovered by hls.js internally. Recreating the
            // player on them resets playback to 0:00, forcing trainees to
            // rewatch mandatory videos from the start.
            if (!canScheduleRecovery) {
              return;
            }
            // Resume from where playback failed instead of starting over.
            const resumeAt = video.currentTime;
            recoveryTimeout = setTimeout(() => {
              recoveryTimeout = null;
              if (!isDisposed) {
                regenHls(resumeAt);
              }
            }, HLS_RECOVERY_DELAY_MS);
          });
        };
        regenHls();
      } else if (
        !isHls(String(props.src)) ||
        video.canPlayType("application/vnd.apple.mpegurl")
      ) {
        // Non-HLS videos or Safari native HLS fallback
        video.src = String(props.src);
      }
    }
    return () => {
      isDisposed = true;
      recoverySeekPositionRef.current = null;
      if (recoveryTimeout !== null) {
        clearTimeout(recoveryTimeout);
      }
      if (hls) {
        hls.destroy();
      }
    };
  }, [ref, props.src, props.language, loadHls]); // eslint-disable-line react-hooks/exhaustive-deps
  const onMouseEnter = useCallback(() => {
    ref.current?.play();
  }, [ref]);
  const onMouseLeave = useCallback(() => {
    ref.current?.pause();
  }, [ref]);

  return (
    <video
      ref={ref}
      src={props.src}
      {...props.videoProps}
      // After the spread, so a videoProps.className doesn't drop hideControls.
      className={cn(
        props.hideControls && "[&::-webkit-media-controls]:hidden",
        props.videoProps?.className,
      )}
      onEnded={props.onVideoComplete}
      onMouseEnter={props.playOnHover ? onMouseEnter : undefined}
      onMouseLeave={props.playOnHover ? onMouseLeave : undefined}
    />
  );
};

export default VideoPlayer;
